import React, { useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import useClipboard from 'react-use-clipboard';
import ReactTooltip from 'react-tooltip';

import {
  TRANSACTION_ITEM_TYPE,
  PHOTO_ITEM_TYPE,
} from '../../../../constants/transactions';
import {
  getBasenameFromEntity,
  getProductBasenames,
} from '../../../../util/role-definitions';
import { formatPriceForDisplay } from '../../../../util/format-money';
import {
  getPhotoItemTypeLabel,
  getTransactionItemRefundedTotal,
} from '../../../../util/transactions';
import { DATE_FORMAT_WITH_TIME } from '../../../../constants/dates';
import Button from '../../../global/Button';
import InfoCircleIcon from '../../../icons/InfoCircleIcon';
import { CURRENCY_SYMBOLS } from '../../../../constants/currency';

const propTypes = {
  account: PropTypes.shape({
    id: PropTypes.string,
    print_partners: PropTypes.shape({
      records: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          print_partner: PropTypes.shape({
            id: PropTypes.string,
          }),
          print_partner_account_id: PropTypes.string,
        }),
      ),
    }),
  }),
  transactionItem: PropTypes.shape({
    id: PropTypes.string,
    transaction: PropTypes.shape({
      id: PropTypes.string,
      uuid: PropTypes.string,
      roes_order_id: PropTypes.string,
    }),
    total_price: PropTypes.shape({
      value: PropTypes.number,
    }),
    item_type: PropTypes.oneOf(Object.keys(TRANSACTION_ITEM_TYPE)),
    photo_item_type: PropTypes.oneOf(Object.keys(PHOTO_ITEM_TYPE)).isRequired,
    album: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    photo: PropTypes.shape({
      id: PropTypes.string,
    }),
    vendor_state_tax_price: PropTypes.shape({
      value: PropTypes.number,
    }),
    vendor_local_tax_price: PropTypes.shape({
      value: PropTypes.number,
    }),
    tax_total_price: PropTypes.shape({
      value: PropTypes.number,
    }),
  }),
  basename: PropTypes.oneOf(getProductBasenames()),
};

const PhotoTransactionDetails = ({ account, transactionItem, basename }) => {
  const {
    album,
    photo,
    prepaid_transaction_item: prepaidTransactionItem,
    purchased_photo_pricing: purchasedPhotoPricing,
    is_prepaid: isPrepaid,
    isConfigured,
  } = transactionItem;
  const {
    uuid: orderNumber,
    order_number: orderShorthand,
    roes_order_id: roesOrderNumber,
    print_partner: printPartner,
    account_address: address,
  } = transactionItem?.transaction ?? {};

  const labId = printPartner
    ? account?.print_partners?.records?.find(
        partner => partner.print_partner.id === printPartner.id,
      )?.print_partner_account_id
    : undefined;

  const [orderNumberCopied, setOrderNumberCopied] = useClipboard(orderNumber);
  const [orderShorthandCopied, setOrderShorthandCopied] =
    useClipboard(orderShorthand);
  const [roesOrderNumberCopied, setRoesOrderNumberCopied] =
    useClipboard(roesOrderNumber);
  /**
   * Returns a list of rows based on the purchased Photo Item type.
   */
  const getPurchasedItemRows = useMemo(() => {
    switch (transactionItem.photo_item_type) {
      case PHOTO_ITEM_TYPE.PRINT: {
        return (
          <Fragment>
            <tr className="print-size-name">
              <td>Purchased Print</td>
              <td>{transactionItem?.photo_print_size_name}</td>
            </tr>
            <tr className="quantity">
              <td>Quantity</td>
              <td>{transactionItem?.quantity}</td>
            </tr>
          </Fragment>
        );
      }
      case PHOTO_ITEM_TYPE.PRINT_PACKAGE: {
        const printProductConfigurations = (
          transactionItem?.print_product_configurations?.records ?? []
        ).filter(config => !!config?.front_print_file_url);

        return (
          <Fragment>
            {printProductConfigurations?.length > 0 && (
              <tr>
                <td>Print File URL</td>
                <td className="wrap">
                  {printProductConfigurations.map((config, idx) => (
                    <Fragment key={config.id}>
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={config.front_print_file_url}
                      >
                        {config.print_product_configuration.name}
                      </a>
                      {idx === printProductConfigurations.length - 1
                        ? ''
                        : ', '}
                    </Fragment>
                  ))}
                </td>
              </tr>
            )}
            <tr>
              <td>{isConfigured ? 'Configured/Sent to Lab' : 'Is Pre-paid'}</td>
              <td>
                {isPrepaid || isConfigured ? (
                  <span className="text-extra-bold text-primary">Yes</span>
                ) : (
                  'No'
                )}
              </td>
            </tr>
            <tr className="print-package-name">
              <td>Purchased Print Package</td>
              <td>{transactionItem?.print_package_name}</td>
            </tr>
            <tr className="quantity">
              <td>Quantity</td>
              <td>{transactionItem?.quantity}</td>
            </tr>
            <tr>
              <td>Purchased Download</td>
              <td>
                {transactionItem?.digital_downloads_purchase ? 'Yes' : 'No'}
              </td>
            </tr>
            <tr>
              <td>Package Price</td>
              <td>
                {
                  CURRENCY_SYMBOLS[
                    transactionItem?.print_package_price?.currency ?? 'USD'
                  ]
                }
                {formatPriceForDisplay(
                  transactionItem?.print_package_price?.value ?? 0,
                )}
              </td>
            </tr>
            <tr>
              <td>Download Price</td>
              <td>
                {
                  CURRENCY_SYMBOLS[
                    transactionItem?.digital_downloads_price?.currency ?? 'USD'
                  ]
                }
                {formatPriceForDisplay(
                  transactionItem?.digital_downloads_price?.value ?? 0,
                )}
              </td>
            </tr>
          </Fragment>
        );
      }
      case PHOTO_ITEM_TYPE.PHOTO_PRICING: {
        const printProductConfiguration = (
          transactionItem?.print_product_configurations?.records ?? []
        ).find(config => !!config?.front_print_file_url);

        return (
          <Fragment>
            {printProductConfiguration && (
              <tr>
                <td>Print File URL</td>
                <td>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={printProductConfiguration.front_print_file_url}
                  >
                    {' '}
                    Open in new tab{' '}
                  </a>
                </td>
              </tr>
            )}
            {printProductConfiguration && (
              <tr>
                <td>Print Product Configuration</td>
                <td>
                  {printProductConfiguration.print_product_configuration?.name}
                </td>
              </tr>
            )}
            <tr>
              <td>{isConfigured ? 'Configured/Sent to Lab' : 'Is Pre-paid'}</td>
              <td>
                {isPrepaid || isConfigured ? (
                  <span className="text-extra-bold text-primary">Yes</span>
                ) : (
                  'No'
                )}
              </td>
            </tr>
            <tr>
              <td>Photo Pricing Model</td>
              <td>{transactionItem?.photo_pricing_name}</td>
            </tr>
            <tr>
              <td>Photo Pricing Item</td>
              <td>
                {transactionItem?.digital_downloads_purchase
                  ? 'Download'
                  : 'Print'}
              </td>
            </tr>
            {!transactionItem?.digital_downloads_purchase && (
              <Fragment>
                <tr className="photo-pricing-print">
                  <td>Print Item</td>
                  <td>
                    {transactionItem?.print_items?.records?.[0]
                      ?.print_package_item_type_name ??
                      transactionItem?.print_product_configurations
                        ?.records?.[0]?.print_product_configuration?.name ??
                      'N/A'}
                  </td>
                </tr>
                <tr className="quantity">
                  <td>Quantity</td>
                  <td>
                    {transactionItem?.print_items?.records?.[0]?.quantity ??
                      transactionItem?.print_product_configurations
                        ?.records?.[0]?.quantity ??
                      purchasedPhotoPricing?.quantity ??
                      'N/A'}
                  </td>
                </tr>
              </Fragment>
            )}
          </Fragment>
        );
      }
      // Downloads have no real details, so nothing returned.
      case PHOTO_ITEM_TYPE.DOWNLOAD:
      default:
        return null;
    }
  }, [transactionItem]);

  return (
    <div>
      <table className="table table-striped mb-3">
        <tbody>
          <tr>
            <td>
              {' '}
              Order Number{' '}
              <Button btnStyle="link" onClick={setOrderNumberCopied}>
                {orderNumberCopied ? 'Copied!' : 'Copy'}
              </Button>
            </td>
            <td>{orderNumber}</td>
          </tr>
          {orderShorthand && (
            <tr>
              <td>
                Order Shorthand{' '}
                <Button btnStyle="link" onClick={setOrderShorthandCopied}>
                  {orderShorthandCopied ? 'Copied!' : 'Copy'}
                </Button>
              </td>
              <td>{orderShorthand}</td>
            </tr>
          )}
          {roesOrderNumber && (
            <tr>
              <td>
                ROES Order Number{' '}
                <Button btnStyle="link" onClick={setRoesOrderNumberCopied}>
                  {roesOrderNumberCopied ? 'Copied!' : 'Copy'}
                </Button>
              </td>
              <td>{roesOrderNumber}</td>
            </tr>
          )}
          {printPartner && (
            <tr>
              <td>Print Partner</td>
              <td>
                {printPartner.name}
                {labId ? ` (Lab ID: ${labId})` : ''}
              </td>
            </tr>
          )}
          <tr>
            <td> Transaction Type </td>
            <td> Photo Purchase </td>
          </tr>
          <tr>
            <td> Date </td>
            <td>
              {moment(transactionItem.created_at).format(DATE_FORMAT_WITH_TIME)}
            </td>
          </tr>
          <tr>
            <td> Item Type </td>
            <td> {getPhotoItemTypeLabel(transactionItem.photo_item_type)} </td>
          </tr>
          <tr>
            <td> Album </td>
            <td>
              {album?.id ? (
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`/app/${
                    getBasenameFromEntity(album) ?? basename
                  }/albums/${album.id}`}
                >
                  {album.name}
                </a>
              ) : (
                <span>{transactionItem?.album_name}</span>
              )}
            </td>
          </tr>
          <tr>
            <td> Photo </td>
            <td>
              {photo?.id && album?.id ? (
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`/app/${
                    getBasenameFromEntity(album) ?? basename
                  }/albums/${album.id}/photos/${photo.id}`}
                >
                  {' '}
                  Open in new tab{' '}
                </a>
              ) : (
                <span> N/A </span>
              )}
            </td>
          </tr>
          {getPurchasedItemRows}
          {address && (
            <tr>
              <td>Shipping Address</td>
              <td>
                <p className="text-light mb-0">{address.name}</p>
                <p className="text-light mb-0">{address.address_line_1}</p>
                <p className="text-light mb-0">{address.address_line_2}</p>
                <p className="text-light mb-0">
                  {[address.city, address.state, address.post_code].join(', ')}
                </p>
                <p className="text-light mb-0">{address.country?.name}</p>
              </td>
            </tr>
          )}
          <tr>
            <td> Amount Paid </td>
            <td>
              {(transactionItem?.total_price?.value ?? 0) >= 0 ? (
                <Fragment>
                  {
                    CURRENCY_SYMBOLS[
                      transactionItem?.total_price?.currency ?? 'USD'
                    ]
                  }
                  {formatPriceForDisplay(
                    transactionItem?.total_price?.value ?? 0,
                  )}
                </Fragment>
              ) : (
                'N/A'
              )}
            </td>
          </tr>
          {(transactionItem?.vendor_state_tax_price?.value ?? 0) > 0 && (
            <tr>
              <td> State Tax Collected </td>
              <td>
                {
                  CURRENCY_SYMBOLS[
                    transactionItem?.vendor_state_tax_price?.currency ?? 'USD'
                  ]
                }
                {formatPriceForDisplay(
                  transactionItem?.vendor_state_tax_price?.value ?? 0,
                )}
              </td>
            </tr>
          )}
          {(transactionItem?.vendor_local_tax_price?.value ?? 0) > 0 && (
            <tr>
              <td> Local Tax Collected </td>
              <td>
                {
                  CURRENCY_SYMBOLS[
                    transactionItem?.vendor_local_tax_price?.currency ?? 'USD'
                  ]
                }
                {formatPriceForDisplay(
                  transactionItem?.vendor_local_tax_price?.value ?? 0,
                )}
              </td>
            </tr>
          )}
          {(transactionItem?.tax_total_price?.value ?? 0) > 0 && (
            <tr>
              <td> Total Tax Collected </td>
              <td>
                {
                  CURRENCY_SYMBOLS[
                    transactionItem?.tax_total_price?.currency ?? 'USD'
                  ]
                }
                {formatPriceForDisplay(
                  transactionItem?.tax_total_price?.value ?? 0,
                )}
              </td>
            </tr>
          )}
          <tr>
            <td> Amount Refunded </td>
            <td>
              {
                CURRENCY_SYMBOLS[
                  transactionItem?.total_price?.currency ?? 'USD'
                ]
              }
              {formatPriceForDisplay(
                getTransactionItemRefundedTotal(transactionItem),
              )}
            </td>
          </tr>
          {prepaidTransactionItem && (
            <tr>
              <td>
                <div className="d-flex justify-content-center align-items-center">
                  Pre-paid Amount{' '}
                  <div
                    className="ml-1"
                    data-for="tooltip-prepaid-amount-tip"
                    data-tip=""
                    data-place="top"
                  >
                    {' '}
                    <InfoCircleIcon size={12} />
                    <ReactTooltip
                      id="tooltip-prepaid-amount-tip"
                      className="tooltip"
                    >
                      Pre-paid amount represents the original <br />
                      amount paid for this pre-paid item. Your <br />
                      customer is not charged again for this <br />
                      configuration transaction.
                    </ReactTooltip>
                  </div>
                </div>
              </td>
              <td>
                {(prepaidTransactionItem?.total_price?.value ?? 0) >= 0 ? (
                  <Fragment>
                    {
                      CURRENCY_SYMBOLS[
                        transactionItem?.total_price?.currency ?? 'USD'
                      ]
                    }
                    {formatPriceForDisplay(
                      (prepaidTransactionItem?.total_price?.value ?? 0) -
                        getTransactionItemRefundedTotal(prepaidTransactionItem),
                    )}
                  </Fragment>
                ) : (
                  'N/A'
                )}
              </td>
            </tr>
          )}
          <tr>
            <td>Final Amount</td>
            <td>
              {(transactionItem?.total_price?.value ?? 0) >= 0 ? (
                <Fragment>
                  {
                    CURRENCY_SYMBOLS[
                      transactionItem?.total_price?.currency ?? 'USD'
                    ]
                  }
                  {formatPriceForDisplay(
                    (transactionItem?.total_price?.value ?? 0) -
                      getTransactionItemRefundedTotal(transactionItem),
                  )}
                </Fragment>
              ) : (
                'N/A'
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

PhotoTransactionDetails.propTypes = propTypes;

export default PhotoTransactionDetails;
